import Duck from 'extensible-duck';
import { getIn, setIn } from 'timm';
import { vendorPortalTourTypes } from './constants';

const indexSelectByValue = (values) =>
  values.reduce((res, item, index) => ((res[item[1]] = item), res), {});

const initialState = {
  showVendorPortalLogin: false,
  autoAcceptInvitationId: null,
  merchantData: {
    isLoading: true,
    selectedMerchant: {},
    showSwitchMerchant: true,
    refreshMerchantData: false,
  },
  tdsCategories: {
    loading: false,
    data: [],
    options: [],
    optionsIndex: {},
  },
  isAnyInvoiceCreated: {
    data: false,
    loading: false,
  },
  vendorPreferences: {
    isLoading: true,
    data: {},
  },
  vendorPortalTourType: vendorPortalTourTypes.NO_TOUR,
};

export default new Duck({
  namespace: 'app',
  store: 'vendorPortal',
  types: [
    'FETCH_TDS_CATEGORIES',
    'FETCH_TDS_CATEGORIES_SUCCESS',
    'FETCH_TDS_CATEGORIES_FAILURE',
    'SET_AUTO_ACCEPT_INVITATION_ID',
    'RESET_AUTO_ACCEPT_INVITATION_ID',
    'SET_SELECTED_MERCHANT',
    'SET_SHOW_SWITCH_MERCHANT',
    'SET_REFRESH_MERCHANT_DATA',
    'SET_SHOW_VENDOR_PORTAL_LOGIN',
    'FETCH_INVOICE_CREATION_STATUS',
    'FETCH_INVOICE_CREATION_STATUS_SUCCESS',
    'FETCH_INVOICE_CREATION_STATUS_FAILURE',
    'SET_INVOICE_CREATION_STATUS',
    'SET_VENDOR_PORTAL_TOUR_TYPE',
    'FETCH_VENDOR_PREFERENCES',
    'FETCH_VENDOR_PREFERENCES_SUCCESS',
    'FETCH_VENDOR_PREFERENCES_FAILURE',
    'SET_VENDOR_PREFERENCES',
  ],
  initialState,
  selectors: {
    getSelectedMerchantId: (state) => getIn(state, ['merchantData', 'selectedMerchant', 'id']),
    getSelectedMerchantName: (state) =>
      getIn(state, ['merchantData', 'selectedMerchant', 'merchant_name']),
    getTdsCategories: (state) => getIn(state, ['tdsCategories', 'data']),
    getVendorPreferences: (state) => getIn(state, ['vendorPreferences', 'data']),
  },
  reducer: (state, action, duck) => {
    const { payload, type } = action;

    switch (type) {
      case duck.types.SET_AUTO_ACCEPT_INVITATION_ID: {
        return setIn(state, ['autoAcceptInvitationId'], payload);
      }

      case duck.types.RESET_AUTO_ACCEPT_INVITATION_ID: {
        return setIn(state, ['autoAcceptInvitationId'], null);
      }

      case duck.types.SET_SELECTED_MERCHANT: {
        const newState = {
          ...state,
          merchantData: {
            ...state.merchantData,
            isLoading: false,
            selectedMerchant: payload,
          },
        };
        return newState;
      }

      case duck.types.FETCH_TDS_CATEGORIES: {
        return setIn(state, ['tdsCategories', 'loading'], true);
      }

      case duck.types.FETCH_TDS_CATEGORIES_FAILURE: {
        return setIn(state, ['tdsCategories', 'loading'], false);
      }

      case duck.types.FETCH_TDS_CATEGORIES_SUCCESS: {
        const data = payload || [];
        const options = [];
        const dataWithDefaults = [];
        const optionsWithoutNoTDS = [];
        const dataWithoutNoTDS = [];

        // Have to set defaults since the framework being used in the microservice
        // does not send fields if they are empty string or 0 or false or null
        data.forEach(({ code = '', name = '', id, slab = 0 } = {}) => {
          const option = [`${code} ${name}`, id];
          options.push(option);
          if (code) {
            optionsWithoutNoTDS.push(option);
            dataWithoutNoTDS.push({ code, name, id, slab });
          }
          dataWithDefaults.push({ code, name, id, slab });
        });

        const optionsIndex = indexSelectByValue(options);

        const newCatgories = {
          loading: false,
          data: dataWithDefaults,
          options,
          optionsIndex,
          optionsWithoutNoTDS,
          optionsWithoutNoTDSIndex: indexSelectByValue(optionsWithoutNoTDS),
          dataWithoutNoTDS,
        };

        return setIn(state, ['tdsCategories'], newCatgories);
      }

      case duck.types.SET_SHOW_SWITCH_MERCHANT: {
        return setIn(state, ['merchantData', 'showSwitchMerchant'], payload);
      }

      case duck.types.SET_REFRESH_MERCHANT_DATA: {
        return setIn(state, ['merchantData', 'refreshMerchantData'], payload);
      }

      case duck.types.SET_SHOW_VENDOR_PORTAL_LOGIN: {
        return setIn(state, ['showVendorPortalLogin'], payload);
      }

      case duck.types.FETCH_INVOICE_CREATION_STATUS: {
        return setIn(state, ['isAnyInvoiceCreated', 'loading'], true);
      }

      case duck.types.FETCH_INVOICE_CREATION_STATUS_FAILURE: {
        return setIn(state, ['isAnyInvoiceCreated', 'loading'], false);
      }

      case duck.types.FETCH_INVOICE_CREATION_STATUS_SUCCESS: {
        const newInvoicesCount = {
          loading: false,
          data: !!payload,
        };
        return setIn(state, ['isAnyInvoiceCreated'], newInvoicesCount);
      }

      case duck.types.SET_VENDOR_PORTAL_TOUR_TYPE: {
        return setIn(state, ['vendorPortalTourType'], payload);
      }

      case duck.types.FETCH_VENDOR_PREFERENCES: {
        return setIn(state, ['vendorPreferences', 'isLoading'], true);
      }

      case duck.types.FETCH_VENDOR_PREFERENCES_FAILURE: {
        return setIn(state, ['vendorPreferences', 'isLoading'], false);
      }

      case duck.types.FETCH_VENDOR_PREFERENCES_SUCCESS: {
        const updatedVendorPreference = {
          isLoading: false,
          data: payload,
        };
        return setIn(state, ['vendorPreferences'], updatedVendorPreference);
      }

      case duck.types.SET_VENDOR_PREFERENCES: {
        const updatedVendorPreference = {
          isLoading: false,
          data: payload,
        };
        return setIn(state, ['vendorPreferences'], updatedVendorPreference);
      }

      default:
        return state;
    }
  },
  creators: (duck) => ({
    setAutoAcceptInvitationId: (id) => {
      return {
        type: duck.types.SET_AUTO_ACCEPT_INVITATION_ID,
        payload: id,
      };
    },
    resetAutoAcceptInvitationId: () => {
      return {
        type: duck.types.RESET_AUTO_ACCEPT_INVITATION_ID,
      };
    },
    setSelectedMerchant: (selectedMerchant) => {
      return {
        type: duck.types.SET_SELECTED_MERCHANT,
        payload: selectedMerchant,
      };
    },
    fetchTDSCategories: () => {
      return {
        type: duck.types.FETCH_TDS_CATEGORIES,
      };
    },
    fetchTDSCategoriesSuccess: (data) => {
      return {
        type: duck.types.FETCH_TDS_CATEGORIES_SUCCESS,
        payload: data,
      };
    },
    fetchTDSCategoriesFailure: () => {
      return {
        type: duck.types.FETCH_TDS_CATEGORIES_FAILURE,
      };
    },
    setShowSwitchMerchant: (data) => {
      return {
        type: duck.types.SET_SHOW_SWITCH_MERCHANT,
        payload: data,
      };
    },
    setRefreshMerchantData: (data) => {
      return {
        type: duck.types.SET_REFRESH_MERCHANT_DATA,
        payload: data,
      };
    },
    setShowVendorPortalLogin: (payload) => {
      return {
        type: duck.types.SET_SHOW_VENDOR_PORTAL_LOGIN,
        payload,
      };
    },
    fetchInvoicesCreationStatus: ({ merchantId }) => {
      return {
        type: duck.types.FETCH_INVOICE_CREATION_STATUS,
        payload: {
          merchantId,
        },
      };
    },
    fetchInvoicesCreationStatusSuccess: (data) => {
      return {
        type: duck.types.FETCH_INVOICE_CREATION_STATUS_SUCCESS,
        payload: data,
      };
    },
    fetchInvoicesCreationStatusFailure: () => {
      return {
        type: duck.types.FETCH_INVOICE_CREATION_STATUS_FAILURE,
      };
    },
    setVendorPortalTourType: (payload) => {
      return {
        type: duck.types.SET_VENDOR_PORTAL_TOUR_TYPE,
        payload,
      };
    },
    fetchVendorPreferences: ({ merchantId }) => {
      return {
        type: duck.types.FETCH_VENDOR_PREFERENCES,
        payload: {
          merchantId,
        },
      };
    },
    fetchVendorPreferencesFailure: () => {
      return {
        type: duck.types.FETCH_VENDOR_PREFERENCES_FAILURE,
      };
    },
    fetchVendorPreferencesSuccess: (payload) => {
      return {
        type: duck.types.FETCH_VENDOR_PREFERENCES_SUCCESS,
        payload,
      };
    },
    setVendorPreferences: (payload) => {
      return {
        type: duck.types.SET_VENDOR_PREFERENCES,
        payload,
      };
    },
  }),
});
